import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";

// Shared login form — single source of truth for both the MyElite app
// and the embedded widget popup. Any change here applies to both flows.
export function LoginForm({ onSuccess, showForgot = true, showRegister = true }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      onSuccess?.(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="login-form">
      {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-xl" data-testid="login-error">{error}</div>}
      <div className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="you@example.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            {showForgot && <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-navy hover:text-orange">Forgot password?</Link>}
          </div>
          <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="••••••••" />
        </div>
      </div>
      <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full mt-6 h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
        {loading ? "Signing in…" : <>Log in <ArrowRight className="h-4 w-4 ml-1" /></>}
      </Button>
      {showRegister && (
        <p className="text-center text-sm text-muted-foreground mt-6">
          No account yet? <Link to="/register" className="font-bold text-navy hover:text-orange" data-testid="to-register">Create one</Link>
        </p>
      )}
    </form>
  );
}
